import { useEffect, useRef } from "react";
import { matchPath, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
    FormikFromProps,
    FormikProductProps,
    initialFromValues,
    initialProductValues,
    initialToValues,
    NewFormikToProps,
    orderFromValidator,
    orderToValidator,
} from "./-components/formik-utils";
import { OrderRequest, usePostOrder } from "common/hooks/user/order/usePostOrder";
import { errorMsgHandler, format12To24 } from "common/hooks/utils/utils";
import { useAuthStore } from "user/store/useAuthStore";
import { useNonMemberStore } from "user/store/useNonMemberStore";
import { useGetCommonTargetCustomer } from "common/hooks/common/useGetCommonTargetCustomer";
import { useGetCommonSender } from "common/hooks/common/useGetCommonSender";

const rootPages = ["/login-non-member", "/new-order"];

export default function NewOrder() {
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const user = useAuthStore(state => state);
    const nonMember = useNonMemberStore(state => state);
    const isIndividualCustomer = user?.m_category === 2; // 개인 회원
    const isNonMember = user.m_category === null;
    const initialCustomerIdx = isNonMember ? nonMember.customer_idx : user.customer_idx;

    const { data: customerData, isLoading } = useGetCommonTargetCustomer(initialCustomerIdx ?? 0);
    const customer_idx = customerData?.data?.customer_idx;
    // 개인회원은 사용할수 없음
    // const { data: senderData, refetch: refetchSender } = useGetCommonSender(
    //     isIndividualCustomer ? "" : initialCustomerIdx?.toString(),
    //     "",
    //     {
    //         // enabled: !isIndividualCustomer && !!customer_idx,
    //     },
    // );
    const { data: senderData, refetch: refetchSender } = useGetCommonSender(initialCustomerIdx?.toString());
    // console.log("senderData", senderData);

    const { mutate, isPending } = usePostOrder();
    const isMatchedRootPages = rootPages.some(page => matchPath(page, pathname));
    const lastToProductIdxRef = useRef<number | null>(null);
    // console.log("customerData", customerData);

    const resetForm = () => {
        formik.from.setValues({
            // ...formik.from.values,
            customer_idx: Number(customerData?.data.customer_idx),
            customer_name: customerData?.data.customer_name ?? "",
            order_name: "",
            order_mobile: "",
        });
        formik.product.resetForm();
        formik.to.resetForm();
        formik.from.setTouched({
            customer_idx: false,
            customer_name: false,
            order_name: false,
            order_mobile: false,
        });
        nonMember.clearData();
    };

    const formik = {
        from: useFormik<FormikFromProps>({
            initialValues: initialFromValues,
            onSubmit: values => {
                navigate("select-category");
            },
            validate: orderFromValidator,
        }),
        product: useFormik<FormikProductProps>({
            initialValues: initialProductValues,
            onSubmit: values => {
                if (values.product_idx !== lastToProductIdxRef.current) {
                    formik.to.setFieldValue("recipient_name", "");
                    formik.to.setFieldValue("recipient_mobile", "");
                }
                lastToProductIdxRef.current = values.product_idx;
                navigate("to");
            },
        }),
        to: useFormik<NewFormikToProps>({
            initialValues: { ...initialToValues, delivery_memo_idx: 1 },
            onSubmit: (values, { setStatus }) => {
                const isSpecialDeliveryHour = values.delivery_hour === "오전중" || values.delivery_hour === "오후중" || values.delivery_hour === "당일배송";
                const delivery_time = isSpecialDeliveryHour
                    ? values.delivery_hour
                    : format12To24(values.delivery_hour, values.delivery_minute) + " " + values.delivery_type;
                const body: OrderRequest = {
                    customer_idx: formik.from.values.customer_idx!,
                    customer_name: formik.from.values.customer_name,
                    order_name: formik.from.values.order_name,
                    order_mobile: formik.from.values.order_mobile,
                    product_idx: Number(formik.product.values.product_idx),
                    wreath_sender: values.wreath_sender, //명의
                    wreath_word: values.wreath_word, //경조사어
                    recipient_name: values.recipient_name,
                    recipient_mobile: values.recipient_mobile,
                    is_union: values.is_union,
                    event_content_idx: values.event_content_idx ?? 1, //경조내용 index
                    event_content: values.event_content,
                    related_person: values.related_person,
                    work_location: values.work_location, //근무지
                    department: values.department, //부서
                    company_tel: values.company_tel, //사번
                    delivery_immediate: values.delivery_immediate, //즉시 배송 여부
                    delivery_date: values.delivery_date, //배송일자 (YYYY-MM-DD)
                    delivery_time: values.delivery_immediate ? "" : delivery_time,
                    delivery_post: values.delivery_post,
                    delivery_address: values.delivery_address,
                    delivery_address_detail: values.delivery_address_detail,
                    delivery_memo_idx: values.delivery_memo_idx!, //배송 메모 index
                    delivery_memo: values.delivery_memo_idx === 1 ? values.display_delivery_memo : values.delivery_memo, //배송 메모
                    file_memo: values.file_memo, //초대장, 청첩장, 부고장 정보 메모
                    order_memo: values.order_memo, //주문 메모
                    order_file: values.order_file,
                };
                // console.log("body", body);
                // return;
                mutate(
                    { ...body },
                    {
                        onSuccess: data => {
                            if (isNonMember) {
                                nonMember.clearData();
                            }
                            // console.log("data", data);
                            const order_idx = data.data.order_idx;
                            // 주문 완료시 완료 페이지 이동
                            navigate(`/order-completed`, { state: { order_idx, customer_idx } });
                        },
                        onError: error => {
                            const err_msg = errorMsgHandler(error);
                            setStatus({
                                state: "error",
                                msg: err_msg,
                            });
                        },
                    },
                );
            },
            validate: orderToValidator,
        }),
    };

    useEffect(() => {
        if (location.state?.reset) resetForm();
    }, [location.state]);

    useEffect(() => {
        if (customerData) {
            formik.from.setValues({
                ...formik.from.values,
                customer_idx: customerData?.data.customer_idx!,
                customer_name: customerData?.data.customer_name ?? "",
                order_name: user.m_name ?? "",
                order_mobile: user.m_mobile ?? "",
            });
        }
    }, [customerData]);

    // 상품 선택시 명의 재 패치
    useEffect(() => {
        refetchSender();
    }, [formik.product.values.union_option]);

    useEffect(() => {
        const isUnion = formik.product.values.union_option;

        if (isUnion) {
            // 노조 상품 선택시
            const targetSenderData = senderData && senderData.data.find(data => data.union_option);
            // console.log("NewOrder targetSenderData", targetSenderData);
            if (targetSenderData) {
                formik.to.setValues({
                    ...formik.to.values,
                    is_union: true,
                    wreath_sender: targetSenderData.union_sender, // 명의
                    wreath_word: targetSenderData.default_sender, // 경조사어
                    // wreath_sender_options: senderData?.data // 명의 셀렉 박스 옵션
                    //     .filter(data => data.union_option)
                    //     .map(data => ({
                    //         idx: data.sender_idx.toString(),
                    //         value: data.union_sender,
                    //     })),
                });
            }
        } else {
            formik.to.setValues({
                ...formik.to.values,
                is_union: false,
                wreath_sender: "", // 명의
                wreath_word: "", // 경조사어
                // 명의 셀렉 박스 옵션
                // wreath_sender_options:
                //     senderData?.data
                //         .filter(data => !data.union_option)
                //         .map(data => ({
                //             idx: data.sender_idx.toString(),
                //             value: data.default_sender,
                //         })) ?? [],
            });
        }
    }, [formik.product.values.union_option, senderData]);

    // if (!isLoading && customerData) return <Navigate to={`from`} replace />;

    return (
        <>
            {!isLoading && isMatchedRootPages && <Navigate to="from" replace />}
            <Outlet context={{ formik, isSubmitting: isPending }} />
        </>
    );
}
